import { Link } from 'react-router-dom';
import type { Entry } from '../types/api';

interface EntryCardProps {
  entry: Entry;
  onDelete?: (id: string) => void;
}

export function EntryCard({ entry, onDelete }: EntryCardProps) {
  const formatDate = (timestamp: number): string => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  const formatDuration = (seconds: number | null): string => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = () => {
    const statusColors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };

    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded ${
          statusColors[entry.transcriptionStatus]
        }`}
      >
        {entry.transcriptionStatus === 'completed' ? 'Transcribed' : entry.transcriptionStatus}
      </span>
    );
  };

  const getMediaIcon = () => {
    if (entry.mediaType === 'video') {
      return (
        <svg
          className="w-5 h-5 text-purple-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
          />
        </svg>
      );
    }
    return (
      <svg
        className="w-5 h-5 text-blue-600"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z"
        />
      </svg>
    );
  };

  const borderColor = entry.mediaType === 'video' ? 'border-l-purple-500' : 'border-l-blue-500';

  return (
    <Link
      to={`/entry/${entry.id}`}
      className={`block bg-white border border-gray-200 border-l-4 ${borderColor} rounded-lg hover:shadow-md transition-shadow self-start cursor-pointer relative`}
    >
      <div className="absolute left-2 top-4">
        {getMediaIcon()}
      </div>
      <div className="pl-10 pr-4 py-4">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            {entry.title && (
              <h3 className="text-lg font-semibold text-gray-900 mb-1">{entry.title}</h3>
            )}
            <p className="text-sm text-gray-500">{formatDate(entry.createdAt)}</p>
            <p className="text-sm font-medium text-gray-700">{formatDuration(entry.duration)}</p>
          </div>
          {getStatusBadge()}
        </div>

        {entry.transcript && (
          <p className="text-sm text-gray-700 line-clamp-3 mb-3">{entry.transcript}</p>
        )}

        <div className="flex items-center justify-between">
          <span className="text-blue-600 text-sm font-medium">
            View Details →
          </span>

          {onDelete && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (confirm('Are you sure you want to delete this entry?')) {
                  onDelete(entry.id);
                }
              }}
              className="text-red-600 hover:text-red-800 text-sm relative z-10"
            >
              Delete
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
